import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {graphql} from 'react-relay';
import styled from 'styled-components';
import {ForecastQueryRenderer} from '../../../ForecastQueryRenderer';
import MissionControlHeader from '../type-script-files/MissionControlHeader';
import Util from '../../../forecast-app/shared/util/util';
import {useForecastFetchQuery} from '../../../forecast-app/shared/hooks/useForecastFetchQuery';
import UpdateNovaAgentMutation from '../../../mutations/nova/UpdateNovaAgentMutation';
import {UpdateNovaAgentInput} from '../../../mutations/nova/__generated__/UpdateNovaAgentMutation.graphql';
import RestoreNovaAgentVersionMutation from '../../../mutations/nova/RestoreNovaAgentVersionMutation';
import AttachNovaAgentFileMutation from '../../../mutations/nova/AttachNovaAgentFileMutation';
import RemoveNovaAgentFileMutation from '../../../mutations/nova/RemoveNovaAgentFileMutation';
import UpdateNovaAgentAudienceMutation from '../../../mutations/nova/UpdateNovaAgentAudienceMutation';
import {UpdateNovaAgentAudienceInput} from '../../../mutations/nova/__generated__/UpdateNovaAgentAudienceMutation.graphql';
import {MutationInput} from '../../../mutations/MutationTypes';
import {NovaStudioShell} from './shell/NovaStudioShell';
import {NovaReferenceFile} from './shell/NovaReferenceFiles';
import {canonicalMimeType} from './shared/novaFileTypes';
import {StudioAgentEntry, StudioCategoryEntry} from './shell/useNovaStudioShell';
import {NovaAgentVersionEntry} from './modals/NovaVersionHistoryModal';
import {toRelayGlobalId} from './shared/relayGlobalId';
import {NovaAgentSavePayload, useNovaAgentDraft} from './state/useNovaAgentDraft';
import {NovaStudioPageDetailQuery$data} from './__generated__/NovaStudioPageDetailQuery.graphql';
import {NovaStudioPageDeploymentQuery$data} from './__generated__/NovaStudioPageDeploymentQuery.graphql';
import {NovaStudioPageDeployDirectoryQuery$data} from './__generated__/NovaStudioPageDeployDirectoryQuery.graphql';
import {DeployDirectory, buildDeployDirectory} from './deploy/novaDeployDirectory';
import {NovaModel, mapNovaModelList} from './shared/novaModelList';
import {AudienceTargets} from './deploy/novaDeploymentData';
import {
	NovaVariable,
	NOVA_FILE_READY_STATUS,
	fileToNovaVariable,
	isNovaVariableLifecycleState,
	isNovaVariableScope,
} from './shared/novaVariableLibrary';

// NovaAgentCategory/NovaAgent only expose the relay-encoded `id` (see
// graphql/src/graphql/type/NovaAgentCategoryType.js, NovaAgentType.js) — there is no separate
// scalar categoryId/agentId field on the category or on the agent's own id, so the raw domain
// id has to be decoded back out of it to key the shell's flat catalogue/agent lists.
export const novaStudioQuery = graphql`
	query NovaStudioPage_Query {
		viewer {
			actualPersonId
			...MissionControlHeader_viewer
			novaAgentCatalogue {
				id
				name
				isUncategorised
				agents(first: 1000) @connection(key: "NovaStudioCatalogue_agents") {
					edges {
						node {
							id
							name
							categoryId
							title
							description
						}
					}
				}
			}
			novaVariableLibrary {
				variables {
					token
					scope
					description
					lifecycleState
				}
				removedVariables {
					token
					scope
					description
					lifecycleState
				}
			}
			novaModels {
				models {
					modelId
					displayName
					hint
					lifecycleState
				}
				defaultModelId
			}
		}
	}
`;

// Separate from `novaStudioQuery` above: the catalogue is metadata-only (deliberately excludes the
// prompt bodies, which can be large and are only ever needed one agent at a time). This is fetched
// on demand — see `NovaStudioPage`'s effect below — whenever the shell's selection changes, rather
// than folded into the catalogue query or fetched for every agent up front.
export const novaAgentDetailQuery = graphql`
	query NovaStudioPageDetailQuery($id: ID!) {
		viewer {
			novaAgent(id: $id) {
				id
				title
				description
				systemPrompt
				userPromptTemplate
				agentMd
				categoryId
				model
				currentVersionNumber
				versions {
					versionNumber
					createdAt
					createdBy
					isCurrent
				}
				files {
					fileId
					filename
					variableToken
					processingStatus
					sizeBytes
					mimeType
				}
			}
		}
	}
`;

// The deployment is its own on-demand read rather than part of the detail query: only the Deploy
// tab needs it, and it costs the service a second round trip.
export const novaDeploymentQuery = graphql`
	query NovaStudioPageDeploymentQuery($id: ID!) {
		viewer {
			# The viewer's own numeric company id, for the "Active for your company" toggle. It is encoded
			# differently from an audience entry's companyId (see encodeCompanyGlobalId in
			# novaDeployDirectory.ts) — this raw id has to be re-encoded with that same function before it
			# can be compared against one.
			company {
				backendId
			}
			novaAgent(id: $id) {
				id
				deployment {
					isLiveToAnyone
					companyCount
					userCount
					companies {
						companyId
					}
					persons {
						personId
						companyId
					}
				}
			}
		}
	}
`;

// The internal company/person directory that feeds the audience picker. Fetched the first time a
// picker opens rather than with the Studio itself: it is every company and every person, and an
// author who never opens Deploy should never pay for it.
export const novaDeployDirectoryQuery = graphql`
	query NovaStudioPageDeployDirectoryQuery {
		viewer {
			missionControlData {
				id
				companies {
					id
					companyId
					name
					accessBlocked
				}
				forecastPersons {
					id
					firstName
					lastName
					email
				}
				otherPersons {
					id
					firstName
					lastName
					email
					companyId
				}
			}
		}
	}
`;

interface CatalogueAgentNode {
	readonly id: string;
	readonly name: string | null;
	readonly categoryId: string | null;
	readonly title: string | null;
	readonly description: string | null;
}

interface CatalogueAgentEdge {
	readonly node: CatalogueAgentNode | null;
}

interface CatalogueCategory {
	readonly id: string;
	readonly name: string | null;
	readonly isUncategorised: boolean | null;
	readonly agents: {
		readonly edges: ReadonlyArray<CatalogueAgentEdge | null> | null;
	} | null;
}

// `useForecastFetchQuery` has no request cancellation: selecting agent A then quickly agent B
// fires two in-flight fetches, and A's response can resolve after B's (or after B's own fetch
// has landed) since there is only one `data` slot. Loading whichever response lands last would
// silently overwrite the currently-selected agent's draft with a different agent's content, which
// a later Save would then persist onto the wrong record — so a response is only ever loaded when
// it still matches the currently selected agent.
export function isDetailResponseForSelectedAgent(
	fetchedAgentGlobalId: string | null | undefined,
	selectedAgentId: string | null
): boolean {
	if (!fetchedAgentGlobalId || !selectedAgentId) {
		return false;
	}
	return fetchedAgentGlobalId === toRelayGlobalId('NovaAgent', selectedAgentId);
}

// Why the detail fetch is re-run for two entirely different reasons, and why only one of them
// should ever load prompt content into the draft:
//   - 'select': the author just opened a (possibly different) agent — the draft has nothing of
//     its own yet for it, so the fetched content becomes the draft's starting point.
//   - 'refresh': a save the draft itself just made succeeded, and this fetch only exists to bring
//     `currentVersionNumber`/`versions` forward. The draft is already the source of truth for
//     prompt content at that point — if it loaded here too, any edit made between the save
//     resolving and this response landing would be silently reverted to the now-stale content this
//     response carries, with no error. So a 'refresh' response is applied nowhere but wherever a
//     later feature reads `currentVersionNumber`/`versions` off it directly.
export type DetailFetchReason = 'select' | 'refresh';

export function shouldLoadPromptContent(reason: DetailFetchReason): boolean {
	return reason === 'select';
}

// Builds the payload `updateNovaAgent` needs. `name` comes from the catalogue record (set at
// create time, not edited from the Prompt tab); the category, prompt bodies and the inline-edited
// title/description come from the working draft. Returns `null` when the draft has no category — so
// the caller can reject the save instead of sending a request the server would reject anyway.
export function buildUpdateNovaAgentInput(
	agent: StudioAgentEntry,
	values: NovaAgentSavePayload
): MutationInput<UpdateNovaAgentInput> | null {
	if (!values.categoryId) {
		return null;
	}
	return {
		id: toRelayGlobalId('NovaAgent', agent.agentId),
		name: agent.name,
		categoryId: values.categoryId,
		title: values.title,
		description: values.description,
		systemPrompt: values.systemPrompt,
		userPromptTemplate: values.userPromptTemplate,
		agentMd: values.agentMd,
		model: values.model,
	};
}

export function mapCatalogueToShellData(catalogue: ReadonlyArray<CatalogueCategory | null> | null): {
	categories: StudioCategoryEntry[];
	agents: StudioAgentEntry[];
} {
	const knownCategories = (catalogue ?? []).filter((category): category is CatalogueCategory => category != null);

	const categories: StudioCategoryEntry[] = knownCategories.map(category => ({
		categoryId: Util.getUUIdFromBase64String(category.id),
		name: category.name ?? '',
		isUncategorised: category.isUncategorised ?? false,
	}));

	const agents: StudioAgentEntry[] = knownCategories.flatMap(category =>
		(category.agents?.edges ?? [])
			.filter((edge): edge is {node: CatalogueAgentNode} => edge?.node != null)
			.map(edge => ({
				agentId: Util.getUUIdFromBase64String(edge.node.id),
				name: edge.node.name ?? '',
				categoryId: edge.node.categoryId ?? '',
				title: edge.node.title ?? '',
				description: edge.node.description ?? '',
			}))
	);

	return {categories, agents};
}

// Full-height column so the MissionControl header sits above a Studio shell that fills the rest of
// the viewport, with the shell's inner scroll areas — the catalogue list and the tab content — and
// never the page itself absorbing the overflow.
//
// The height is set here, in viewport units, because this route renders outside the logged-in app
// frame: the MissionControl routes mount their page component directly, with no `100vh` flex column
// above them, so neither `flex: 1` nor `height: 100%` resolves to anything definite here and the
// column would grow to its content instead (measured: a 31-agent catalogue rendering 1214px tall in
// a 1080px viewport, scrolling the whole document and un-pinning the New-agent action). This column
// is the topmost element of the page — the MissionControl header is inside it — so the viewport
// height is exactly the budget it has to divide. `min-height: 0` lets its flex children shrink below
// their content, and `overflow: hidden` makes an unbounded descendant a visible bug here rather than
// a silent document-level scrollbar.
const StudioPageColumn = styled.div`
	display: flex;
	flex-direction: column;
	height: 100vh;
	min-height: 0;
	overflow: hidden;
`;

interface NovaStudioPageRelayProps {
	viewer: {
		novaAgentCatalogue: ReadonlyArray<CatalogueCategory | null> | null;
		novaVariableLibrary: {
			variables: ReadonlyArray<NovaVariable | null> | null;
			removedVariables: ReadonlyArray<NovaVariable | null> | null;
		} | null;
		novaModels: {
			models: ReadonlyArray<RawNovaModel | null> | null;
			defaultModelId: string | null;
		} | null;
	};
}

// The raw, fully-nullable shape the model list arrives in, for the same reason the variable library
// has one: it is proxied verbatim from an externally-owned registry, so nothing about an entry is
// guaranteed. mapNovaModelList drops what it cannot use.
interface RawNovaModel {
	readonly modelId: string | null;
	readonly displayName: string | null;
	readonly hint: string | null;
	readonly lifecycleState: string | null;
}

// The raw, fully-nullable shape the library query returns. Every field can be null (the library is
// proxied verbatim from an externally-owned registry), so this maps into the clean, non-null
// `NovaVariable[]` the rest of the UI relies on — dropping anything unusable rather than trusting it.
interface RawNovaVariable {
	readonly token: string | null;
	readonly scope: string | null;
	readonly description: string | null;
	readonly lifecycleState: string | null;
}

export interface NovaVariableLibraryData {
	offered: NovaVariable[];
	removed: NovaVariable[];
}

function mapVariables(raw: ReadonlyArray<RawNovaVariable | null> | null | undefined): NovaVariable[] {
	const mapped: NovaVariable[] = [];
	for (const variable of raw ?? []) {
		// A variable with no token or an unrecognised scope can't be inserted or grouped, so it is
		// skipped rather than rendered broken. An unknown lifecycle state falls back to ACTIVE.
		if (!variable || !variable.token || !isNovaVariableScope(variable.scope)) {
			continue;
		}
		const lifecycleState = isNovaVariableLifecycleState(variable.lifecycleState) ? variable.lifecycleState : 'ACTIVE';
		// A variable the registry marks removed must never be offered for insertion, even if the same
		// payload also lists it in `variables` — otherwise the panel renders it as a normal, clickable
		// row and clicking it immediately reports it as a broken reference.
		if (lifecycleState === 'REMOVED') {
			continue;
		}
		mapped.push({
			token: variable.token,
			scope: variable.scope,
			description: variable.description ?? '',
			lifecycleState,
		});
	}
	return mapped;
}

// Removed entries only need a usable token to do their one job — letting a lingering reference read
// as broken rather than unknown. Scope is only ever read to group *offered* rows in the panel, which
// a removed entry never enters, so requiring a recognised scope here (as `mapVariables` does for
// offered rows) would drop a variable the registry retired at the same time as its scope, losing
// exactly the distinction this list exists to draw. An unrecognised scope or lifecycle state is
// therefore defaulted rather than treated as disqualifying.
function mapRemovedVariables(raw: ReadonlyArray<RawNovaVariable | null> | null | undefined): NovaVariable[] {
	const mapped: NovaVariable[] = [];
	for (const variable of raw ?? []) {
		if (!variable || !variable.token) {
			continue;
		}
		mapped.push({
			token: variable.token,
			scope: isNovaVariableScope(variable.scope) ? variable.scope : 'TASK',
			description: variable.description ?? '',
			lifecycleState: isNovaVariableLifecycleState(variable.lifecycleState) ? variable.lifecycleState : 'REMOVED',
		});
	}
	return mapped;
}

// Splits the library into what authors may insert and what only exists so a lingering reference to
// it can be named as broken rather than unknown. The two are disjoint by construction: `mapVariables`
// excludes anything the registry marks removed, and nothing resolves a removed variable as offered.
export function mapVariableLibrary(
	library:
		| {
				readonly variables: ReadonlyArray<RawNovaVariable | null> | null;
				readonly removedVariables?: ReadonlyArray<RawNovaVariable | null> | null;
		  }
		| null
		| undefined
): NovaVariableLibraryData {
	return {offered: mapVariables(library?.variables), removed: mapRemovedVariables(library?.removedVariables)};
}

export interface NovaStudioPageContentProps {
	categories: StudioCategoryEntry[];
	agents: StudioAgentEntry[];
	variables: NovaVariable[];
	removedVariables: NovaVariable[];
	// The engineering-owned model list, fetched once and shared by the Prompt-tab picker, the
	// New-agent modal and the dry-run output card. It fed a fourth surface until the Deploy tab was
	// rebuilt as one audience per agent: with no stages and no pinned versions there is nothing left
	// to warn about, so that consumer is gone rather than pending.
	models: NovaModel[];
	defaultModelId: string | null;
	onRefetchCatalogue?: () => void;
}

// Split out from `NovaStudioPage` because it needs its own hooks (the working draft, the
// on-demand detail fetch) — those must live in a real component's body, not in the
// `ForecastQueryRenderer` render-prop callback itself, since that callback isn't guaranteed to
// run on every render of the same fiber (e.g. while the catalogue query is loading).
export function NovaStudioPageContent({
	categories,
	agents,
	variables,
	removedVariables,
	models,
	defaultModelId,
	onRefetchCatalogue,
}: NovaStudioPageContentProps): JSX.Element {
	const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
	// Bumped after a successful save so the detail-fetch effect below re-runs for the *same*
	// selected agent — the version list/current version a later task reads off it are only ever
	// as fresh as this fetch, and a save is exactly the event that moves them forward.
	const [detailRefreshToken, setDetailRefreshToken] = useState(0);
	// Which of the two reasons above triggered the most recent (or in-flight) detail fetch — read
	// by the load effect below to decide whether this response is allowed to touch the draft.
	const [detailFetchReason, setDetailFetchReason] = useState<DetailFetchReason>('select');
	// The agentId whose real content was last successfully loaded into the draft via a 'select'
	// response (see the load effect below). `useForecastFetchQuery` surfaces no error state of its
	// own, so without tracking this separately, a detail fetch that fails (or simply hasn't landed
	// yet) for the selected agent would leave the draft on its initial empty/stale values with no
	// indication anything is wrong — the save-state pill would still read "Saved", and a Save from
	// there would silently persist blank prompt bodies over the agent's real content. Comparing this
	// against `selectedAgentId` (see `isDetailLoadedForSelected` below) is what Save is gated on.
	const [loadedAgentId, setLoadedAgentId] = useState<string | null>(null);

	// Selection changes are the only path that should ever seed the draft from fetched content, so
	// this is the only place `detailFetchReason` is ever set to 'select' — everywhere else that
	// triggers a fetch (i.e. a save) sets it to 'refresh' instead (see `handleSave` below).
	const handleSelectedAgentIdChange = useCallback((agentId: string | null): void => {
		setDetailFetchReason('select');
		setSelectedAgentId(agentId);
	}, []);

	// The live selection, readable from a mutation callback that resolved long after it was created.
	// A callback closes over the selection as it was at call time, so it cannot tell on its own whether
	// the author has since moved to another agent — see requestDetailRefresh below.
	const selectedAgentIdRef = useRef(selectedAgentId);
	selectedAgentIdRef.current = selectedAgentId;

	// Ask the detail fetch to re-run for an agent, from a mutation callback that has just changed it.
	//
	// The selection guard is the point. Both reasons misfire if the author has moved on in the meantime:
	//   - 'refresh' responses are barred from touching the draft (see shouldLoadPromptContent), so
	//     setting it for a stale agent makes the *new* agent's in-flight 'select' response land under
	//     'refresh'. Its content is never loaded, `loadedAgentId` stays on the previous agent, and the
	//     editors show the previous agent's prompt bodies under the new agent's header with Save
	//     disabled. Re-selecting the same agent doesn't re-fire the fetch effect, so that only clears by
	//     switching to a third agent and back.
	//   - 'select' does the opposite and reloads the draft, discarding whatever the author has typed
	//     into the agent they switched to.
	// Saves and restores are round-trips of milliseconds and rarely raced; a file upload is seconds,
	// which turns a narrow window into a routine one.
	const requestDetailFetch = useCallback((agentIdAtCallTime: string | null, reason: DetailFetchReason): void => {
		if (selectedAgentIdRef.current !== agentIdAtCallTime) {
			return;
		}
		setDetailFetchReason(reason);
		setDetailRefreshToken(token => token + 1);
	}, []);

	const handleSave = useCallback(
		(values: NovaAgentSavePayload): Promise<void> =>
			new Promise((resolve, reject) => {
				const agentIdAtCallTime = selectedAgentId;
				const agent = agents.find(candidate => candidate.agentId === agentIdAtCallTime);
				if (!agent) {
					reject(new Error('save requires a selected agent'));
					return;
				}
				const input = buildUpdateNovaAgentInput(agent, values);
				if (!input) {
					reject(new Error('updateNovaAgent requires a categoryId'));
					return;
				}
				Util.CommitMutation(
					UpdateNovaAgentMutation,
					input,
					() => {
						// The agent's connection membership is keyed off its category on the server;
						// Relay merges the updated node by id but can't move it between connections on
						// its own, so refetch when the category actually changed (mirrors the edit
						// modal's own save path in NovaStudioShell.tsx).
						if (values.categoryId !== agent.categoryId) {
							onRefetchCatalogue?.();
						}
						requestDetailFetch(agentIdAtCallTime, 'refresh');
						resolve();
					},
					true,
					null,
					(transaction, onFailure) => {
						// The fallback returns true when it has scheduled a retry of this same
						// mutation instead of surfacing an error (a transient network failure). That
						// isn't a final failure, so don't reject: rejecting would flip the draft to
						// its Error save-state while the retry is still in flight, and a retry that
						// then succeeds would leave the pill reading "error" over an agent that was
						// in fact saved. The retry's own success/failure settles this promise.
						if (onFailure(transaction) === true) {
							return;
						}
						reject(new Error('updateNovaAgent mutation failed'));
					}
				);
			}),
		[agents, selectedAgentId, onRefetchCatalogue, requestDetailFetch]
	);

	const detailFetch = useForecastFetchQuery<NovaStudioPageDetailQuery$data, {id: string}>(novaAgentDetailQuery);
	// A detail response can arrive for a since-abandoned selection (useForecastFetchQuery has no
	// cancellation) — only read or merge it while it still matches the current selection. This is the
	// same staleness guard the draft-loading effect below uses.
	const detailAgent = detailFetch.data?.viewer?.novaAgent;
	const isDetailForSelectedAgent = detailAgent != null && isDetailResponseForSelectedAgent(detailAgent.id, selectedAgentId);

	// The selected agent's attached documents — a list for the Reference files section, and (for READY
	// files) citable {file:...} variables merged onto the library. The merge is what lets insertion,
	// permission and broken-reference checks all see file variables (see novaInputValidation).
	const referenceFiles = useMemo<NovaReferenceFile[]>(() => {
		if (!isDetailForSelectedAgent || !detailAgent) {
			return [];
		}
		return (detailAgent.files ?? []).map(file => ({
			fileId: file.fileId,
			filename: file.filename,
			variableToken: file.variableToken,
			processingStatus: file.processingStatus,
			sizeBytes: file.sizeBytes ?? null,
		}));
	}, [isDetailForSelectedAgent, detailAgent]);

	const mergedVariables = useMemo<NovaVariable[]>(
		() => [
			...variables,
			...referenceFiles.filter(file => file.processingStatus === NOVA_FILE_READY_STATUS).map(fileToNovaVariable),
		],
		[variables, referenceFiles]
	);

	const draft = useNovaAgentDraft({onSave: handleSave, initialModel: defaultModelId});

	useEffect(() => {
		if (!selectedAgentId) {
			return;
		}
		// No onSuccess override: the effect below (keyed on `detailFetch.data`) is what actually loads
		// a matching response into the draft. onError only exists so a failed fetch is a known failure
		// rather than silent success — `loadedAgentId` simply never advances to this agent, which is
		// exactly what keeps Save blocked via `isDetailLoadedForSelected` below.
		detailFetch.fetch({id: toRelayGlobalId('NovaAgent', selectedAgentId)}, undefined, () => undefined);
		// Deliberately keyed on the selected agent id and the refresh token alone: `detailFetch.fetch`
		// is a fresh closure every render (see useForecastFetchQuery), so including it here would
		// re-fetch on every render rather than once per selection (or once per explicit refresh —
		// see `detailRefreshToken` above).
	}, [selectedAgentId, detailRefreshToken]);

	useEffect(() => {
		const agent = detailFetch.data?.viewer?.novaAgent;
		if (!agent || !isDetailResponseForSelectedAgent(agent.id, selectedAgentId)) {
			// Either nothing came back, or this response belongs to an agent that isn't the current
			// selection anymore (a stale, out-of-order fetch — see isDetailResponseForSelectedAgent) —
			// either way, there is nothing to load into the current draft.
			return;
		}
		if (!shouldLoadPromptContent(detailFetchReason)) {
			// A save-triggered refresh, not a selection — see `shouldLoadPromptContent` for why this
			// response must never reach `draft.load`: the draft already holds whatever the author has
			// typed since the save (possibly further edits made after it resolved), and loading here
			// would silently revert those to this now-stale snapshot.
			return;
		}
		// The detail fetch is the source for all of the draft's fields: the prompt bodies plus the
		// title/description/category, which the graphql layer promotes from the agent's current
		// version (the catalogue only carries name + categoryId, so it can't seed title/description).
		draft.load(
			{
				systemPrompt: agent.systemPrompt ?? '',
				userPromptTemplate: agent.userPromptTemplate ?? '',
				agentMd: agent.agentMd ?? '',
			},
			agent.categoryId ?? null,
			agent.title ?? '',
			agent.description ?? '',
			// Falls back to the platform default only when the record genuinely has no model - the
			// service backfills a record stored before the field existed at read time, so this is
			// the belt to that braces.
			agent.model ?? defaultModelId
		);
		// The draft now genuinely holds this agent's real content — record it as loaded so Save can
		// unblock (see `isDetailLoadedForSelected` below).
		setLoadedAgentId(selectedAgentId);
		// Deliberately keyed on the fetch result, the current selection, and the fetch reason alone:
		// `draft.load` is recreated every render along with the rest of the draft, so including it
		// here would re-run on every render rather than only when a new fetch result (or a newer
		// selection/reason that invalidates a stale one) actually calls for it.
	}, [detailFetch.data, selectedAgentId, detailFetchReason]);

	// The essential correctness gate: Save must never fire before the selected agent's real content
	// has actually been loaded into the draft (see `loadedAgentId` above for why this can't just be
	// "the fetch resolved" — a 'refresh' response never touches the draft either, so only a matching
	// 'select' response advances this). Threaded down to NovaStudioShell as `canSave`, which combines
	// it with the existing dirty/in-flight checks in `isSaveDisabled`.
	const isDetailLoadedForSelected = selectedAgentId != null && loadedAgentId === selectedAgentId;

	const versions: NovaAgentVersionEntry[] = useMemo(() => {
		if (!isDetailForSelectedAgent || !detailAgent) {
			return [];
		}
		return detailAgent.versions.map(version => ({
			versionNumber: version.versionNumber,
			createdAt: version.createdAt ?? null,
			createdBy: version.createdBy ?? null,
			isCurrent: version.isCurrent,
		}));
	}, [isDetailForSelectedAgent, detailAgent]);

	// Restoring a version is, from the draft's point of view, exactly like opening a different
	// agent: the fetched content becomes the new starting point and any local edits are meant to
	// be discarded (that's the whole point of restoring) — so this reuses the 'select' detail-fetch
	// reason (see `shouldLoadPromptContent`) to load the restored content once the follow-up fetch
	// below lands, rather than introducing a third reason that would behave identically.
	const handleRestoreVersion = useCallback(
		(versionNumber: number): Promise<void> =>
			new Promise((resolve, reject) => {
				const agentIdAtCallTime = selectedAgentId;
				if (!agentIdAtCallTime) {
					reject(new Error('restore requires a selected agent'));
					return;
				}
				Util.CommitMutation(
					RestoreNovaAgentVersionMutation,
					{id: toRelayGlobalId('NovaAgent', agentIdAtCallTime), versionNumber},
					() => {
						requestDetailFetch(agentIdAtCallTime, 'select');
						resolve();
					},
					true,
					null,
					(transaction, onFailure) => {
						// Same retry-in-flight guard as the save path above: a scheduled retry is not
						// a final failure, so the version-history modal keeps its rows disabled until
						// the retry itself settles instead of re-enabling them mid-flight.
						if (onFailure(transaction) === true) {
							return;
						}
						reject(new Error('restoreNovaAgentVersion mutation failed'));
					}
				);
			}),
		[selectedAgentId, requestDetailFetch]
	);

	// Attaching or removing a reference document is agent-scoped and does not touch the draft, so both
	// use the 'refresh' detail-fetch reason: the re-run brings the new `files` (and the Files-scope
	// variables derived from them) forward without reloading — and so silently reverting — any prompt
	// edits the author has made. The raw File rides as a Relay uploadable (5th arg); the server streams
	// it to S3 and records the metadata.
	const handleAttachFile = useCallback(
		(file: File): Promise<void> =>
			new Promise((resolve, reject) => {
				const agentIdAtCallTime = selectedAgentId;
				if (!agentIdAtCallTime) {
					reject(new Error('attach requires a selected agent'));
					return;
				}
				Util.CommitMutation(
					AttachNovaAgentFileMutation,
					{
						id: toRelayGlobalId('NovaAgent', agentIdAtCallTime),
						filename: file.name,
						// Derive the MIME from the extension: the browser's File.type is empty/inconsistent
						// for several supported types (.md, .csv), and the server validates by MIME. The
						// component only ever attaches files that pass validateNovaFile, so this is non-null.
						mimeType: canonicalMimeType(file.name) ?? file.type,
						sizeBytes: file.size,
					},
					() => {
						requestDetailFetch(agentIdAtCallTime, 'refresh');
						resolve();
					},
					true,
					{file},
					(transaction, onFailure) => {
						if (onFailure(transaction) === true) {
							return;
						}
						reject(new Error('attachNovaAgentFile mutation failed'));
					}
				);
			}),
		[selectedAgentId, requestDetailFetch]
	);

	const handleRemoveFile = useCallback(
		(fileId: string): Promise<void> =>
			new Promise((resolve, reject) => {
				const agentIdAtCallTime = selectedAgentId;
				if (!agentIdAtCallTime) {
					reject(new Error('remove requires a selected agent'));
					return;
				}
				Util.CommitMutation(
					RemoveNovaAgentFileMutation,
					{id: toRelayGlobalId('NovaAgent', agentIdAtCallTime), fileId},
					() => {
						requestDetailFetch(agentIdAtCallTime, 'refresh');
						resolve();
					},
					true,
					null,
					(transaction, onFailure) => {
						if (onFailure(transaction) === true) {
							return;
						}
						reject(new Error('removeNovaAgentFile mutation failed'));
					}
				);
			}),
		[selectedAgentId, requestDetailFetch]
	);

	const deploymentFetch = useForecastFetchQuery<NovaStudioPageDeploymentQuery$data, {id: string}>(novaDeploymentQuery, true);
	const directoryFetch = useForecastFetchQuery<NovaStudioPageDeployDirectoryQuery$data, Record<string, never>>(
		novaDeployDirectoryQuery
	);

	// `useForecastFetchQuery` returns a fresh object — and a fresh `fetch` — on every render, so any
	// callback that lists it as a dependency changes identity on every render too. The shell triggers
	// the deployment re-read from an effect keyed on that callback, and `fetch` immediately sets state,
	// so an unstable identity there means: effect fires, fetch sets state, re-render, new identity,
	// effect fires again — an unbounded request loop rather than one read.
	//
	// Holding the latest hook result in a ref keeps these callbacks stable for the lifetime of the
	// page while still calling the current `fetch`.
	const deploymentFetchRef = useRef(deploymentFetch);
	deploymentFetchRef.current = deploymentFetch;
	const directoryFetchRef = useRef(directoryFetch);
	directoryFetchRef.current = directoryFetch;

	// A write's own response, held until the next read replaces it. The service re-reads the partition
	// with ConsistentRead so the mutation payload is authoritative, which makes a follow-up fetch pure
	// cost — and worse than free, because `clearDataOnFetch` blanks `deploymentFetch.data` for the
	// duration, so the tab spent every refetch asserting the agent serves nobody, immediately after
	// the write that made it live. Cleared on selection change and whenever a real read is started, so
	// reopening the tab still shows what the service holds rather than this cached write.
	const [writtenDeployment, setWrittenDeployment] = useState<{agentId: string; deployment: unknown} | null>(null);

	// A failed deployment read is not the same as an agent that serves nobody, and `useForecastFetchQuery`
	// surfaces no error of its own — its rejection handler only clears `loading`. Without this the tab
	// would render an empty deployment and the live-to-nobody banner indefinitely, with nothing saying
	// the read had failed.
	const [hasDeploymentReadFailed, setHasDeploymentReadFailed] = useState(false);

	// Re-read after every deployment write, and whenever the Deploy tab is opened for an agent, so
	// the tab always shows what the service actually holds rather than an optimistic guess.
	const refetchDeployment = useCallback((agentId: string) => {
		setWrittenDeployment(null);
		setHasDeploymentReadFailed(false);
		deploymentFetchRef.current.fetch({id: toRelayGlobalId('NovaAgent', agentId)}, undefined, () =>
			setHasDeploymentReadFailed(true)
		);
	}, []);

	// Same staleness window as the detail fetch above (see isDetailResponseForSelectedAgent): this
	// fetch has no request cancellation either, and is re-triggered both by a successful audience
	// mutation and by reopening the Deploy tab, so a response for an agent the author has since
	// navigated away from must never be shown as if it were the selected agent's deployment.
	// Read off the same query as the deployment itself (see novaDeploymentQuery's own comment on why
	// it carries this) rather than the Studio's page-level viewer, since only this on-demand query
	// asks for it. Re-encoded, right before ever being compared against an audience entry, in
	// NovaDeployTab — see its own comment for why comparing the two raw values would never match.
	const viewerCompanyId = deploymentFetch.data?.viewer?.company?.backendId ?? null;

	const fetchedDeploymentAgent = deploymentFetch.data?.viewer?.novaAgent;
	const isDeploymentForSelectedAgent = isDetailResponseForSelectedAgent(fetchedDeploymentAgent?.id, selectedAgentId);
	const fetchedDeployment = isDeploymentForSelectedAgent ? fetchedDeploymentAgent?.deployment ?? null : null;

	const deployment = (
		writtenDeployment && writtenDeployment.agentId === selectedAgentId ? writtenDeployment.deployment : fetchedDeployment
	) as typeof fetchedDeployment;

	// A write's payload only stands in for a read when it actually carries one — a mutation that
	// returned no deployment (an older client, a partial response) falls back to re-reading rather
	// than leaving the tab on a stale write.
	const adoptWrittenDeployment = useCallback(
		(agentId: string, written: unknown): void => {
			if (written === null || written === undefined) {
				refetchDeployment(agentId);
				return;
			}
			setHasDeploymentReadFailed(false);
			setWrittenDeployment({agentId, deployment: written});
		},
		[refetchDeployment]
	);

	// Whether an audience mutation is currently in flight — the Deploy tab only ever has one submit
	// control active (the audience modal) at a time and disables the rest of its own controls while
	// it is open.
	const [isDeploySubmitting, setIsDeploySubmitting] = useState(false);

	const directory = useMemo<DeployDirectory>(
		() => buildDeployDirectory(directoryFetch.data?.viewer?.missionControlData),
		[directoryFetch.data]
	);

	// Fetched the first time a picker opens rather than with the Studio itself — see the query's own
	// comment above for why. Guarded so re-opening a picker (or opening a second one) doesn't refire
	// the fetch once the directory has already loaded or is already in flight.
	const handleOpenDirectory = useCallback(() => {
		const {data, loading, fetch} = directoryFetchRef.current;
		if (data || loading) {
			return;
		}
		fetch({});
	}, []);

	const submitAudienceInput = useCallback(
		(agentId: string, input: MutationInput<UpdateNovaAgentAudienceInput>): void => {
			setIsDeploySubmitting(true);
			Util.CommitMutation(
				UpdateNovaAgentAudienceMutation,
				input,
				response => {
					setIsDeploySubmitting(false);
					adoptWrittenDeployment(agentId, response?.updateNovaAgentAudience?.agent?.deployment);
				},
				true,
				null,
				(transaction, onFailure) => {
					// Same retry-in-flight guard as the save path above: a scheduled retry is not a
					// final failure, so the tab keeps its controls disabled until the retry itself
					// settles instead of re-enabling them mid-flight.
					if (onFailure(transaction) === true) {
						return;
					}
					setIsDeploySubmitting(false);
				}
			);
		},
		[adoptWrittenDeployment]
	);

	const handleAudienceSubmit = useCallback(
		(add: AudienceTargets, remove: AudienceTargets) => {
			if (!selectedAgentId) {
				return;
			}
			submitAudienceInput(selectedAgentId, {id: toRelayGlobalId('NovaAgent', selectedAgentId), add, remove});
		},
		[selectedAgentId, submitAudienceInput]
	);

	return (
		<NovaStudioShell
			categories={categories}
			agents={agents}
			draft={draft}
			variables={mergedVariables}
			removedVariables={removedVariables}
			models={models}
			defaultModelId={defaultModelId}
			versions={versions}
			onRestoreVersion={handleRestoreVersion}
			canSave={isDetailLoadedForSelected}
			onSelectedAgentIdChange={handleSelectedAgentIdChange}
			onRefetchCatalogue={onRefetchCatalogue}
			referenceFiles={referenceFiles}
			onAttachFile={handleAttachFile}
			onRemoveFile={handleRemoveFile}
			deployment={deployment}
			directory={directory}
			isDirectoryLoading={directoryFetch.loading}
			isDeploySubmitting={isDeploySubmitting}
			isDeploymentLoading={deploymentFetch.loading}
			hasDeploymentReadFailed={hasDeploymentReadFailed}
			viewerCompanyId={viewerCompanyId}
			onAudienceSubmit={handleAudienceSubmit}
			onOpenDirectory={handleOpenDirectory}
			onDeployTabOpened={refetchDeployment}
		/>
	);
}

export default function NovaStudioPage(): JSX.Element {
	return (
		<ForecastQueryRenderer
			query={novaStudioQuery}
			variables={{}}
			render={(relayProps: NovaStudioPageRelayProps, retry: (() => void) | null) => {
				const {categories, agents} = mapCatalogueToShellData(relayProps.viewer.novaAgentCatalogue);
				const library = mapVariableLibrary(relayProps.viewer.novaVariableLibrary);
				const modelList = mapNovaModelList(relayProps.viewer.novaModels);
				// A new category, or moving an agent between categories, can't be patched into the
				// store, so the shell asks for a full refetch via the render-prop's retry.
				return (
					<StudioPageColumn>
						{/* The viewer carries `...MissionControlHeader_viewer` at runtime (see the query
						    above); the render-prop's viewer type is intentionally loose, so narrow it to
						    exactly the header's expected fragment ref here. */}
						<MissionControlHeader
							viewer={relayProps.viewer as unknown as React.ComponentProps<typeof MissionControlHeader>['viewer']}
						/>
						<NovaStudioPageContent
							categories={categories}
							agents={agents}
							variables={library.offered}
							removedVariables={library.removed}
							models={modelList.models}
							defaultModelId={modelList.defaultModelId}
							onRefetchCatalogue={retry ?? undefined}
						/>
					</StudioPageColumn>
				);
			}}
		/>
	);
}
